import styled, { css } from 'styled-components'
import { useFormContext } from 'react-hook-form'
import { ReactHookFc, ReactHookForm } from 'src/components/common/ReactHookForm'
import { Noto_body1_b16, Noto_heading3_b20 } from 'src/styles/fonts/NotoSansKrFonts'
import { Typography } from 'src/components/common/Typography'
import { Container } from 'src/components/common/Container'
import { JoinOtpCertFormItemsType } from 'src/types/react-hook-form'
import { JoinLinkPageProps } from 'src/pages/JoinLinkPage/index'
import joinLinkBanner from 'src/assets/images/join_link/join_link_banner.png'
import RenderIf from 'src/components/common/RenderIf'
import { ButtonOption3 } from 'src/components/common/Button'
import arrowMotion from 'src/assets/images/join_link/arrow_motion.gif'
import 'pretendard/dist/web/static/pretendard.css'

const JoinLinkPageView = ({
  registerAttribute,
  onOtpCertVerifySubmit,
  onOtpCertRequestSubmit,
  onOtpCertRequestError,
  onOtpCertVerifyError,
  count,
  isOtpStep,
}: JoinLinkPageProps) => {
  const { handleSubmit: otpRequestHandleSubmit } =
    useFormContext<Pick<JoinOtpCertFormItemsType, 'pNumber' | 'agency' | 'certNumber'>>()
  const { handleSubmit: otpCertHandleSubmit } = useFormContext<JoinOtpCertFormItemsType>()
  const { watch, getValues } = useFormContext<JoinOtpCertFormItemsType>()
  const certNumberValid = watch('certNumber')?.length === 4

  const handleOtpRequestSubmit = otpRequestHandleSubmit(
    onOtpCertRequestSubmit,
    onOtpCertRequestError,
  )
  const handleOtpCertSubmit = otpCertHandleSubmit(onOtpCertVerifySubmit, onOtpCertVerifyError)

  const telecomPrefix =
    getValues('agency') === 0 ? 'SKT' : getValues('agency') === 1 ? 'KT' : 'LGU+'

  return (
    <S.JoinLinkPageWrap $option="flex_column">
      <img src={joinLinkBanner} alt="배너 이미지" />
      <ReactHookForm onSubmit={handleOtpCertSubmit} style={S.JoinLinkFormStyle}>
        <RenderIf when={!isOtpStep}>
          <S.InputWrap $option="flex_row" $column_gap="12px" $disabled>
            <S.TelecomPrefix>{telecomPrefix}</S.TelecomPrefix>
            <S.Divider />
            <ReactHookFc.Input {...registerAttribute.pNumber} style={S.InputStyle} disabled />
          </S.InputWrap>
          <S.SubmitButton onClick={handleOtpRequestSubmit}>
            <span>쿠폰 확인하기</span>
            <img src={arrowMotion} alt="쿠폰 확인하기" />
          </S.SubmitButton>
        </RenderIf>
        <RenderIf when={isOtpStep}>
          <Container $option="flex_row" $column_gap="6px">
            <S.InputWrap $option="flex_row_space_between" $isActive={certNumberValid}>
              <ReactHookFc.Input
                {...registerAttribute.certNumber}
                style={S.InputStyle}
                placeholder="인증번호 입력"
              />
              <Typography $fontStyle={Noto_body1_b16} $color="var(--black-70)">
                {count?.substring(4, 12)?.replaceAll(' ', '')}
              </Typography>
            </S.InputWrap>
            <S.ResendButton type="button" onClick={handleOtpRequestSubmit}>
              재발송
            </S.ResendButton>
          </Container>
          <S.SubmitButton onClick={handleOtpCertSubmit} disabled={!certNumberValid}>
            확인
          </S.SubmitButton>
        </RenderIf>
      </ReactHookForm>
    </S.JoinLinkPageWrap>
  )
}
export default JoinLinkPageView

const S = {
  JoinLinkPageWrap: styled(Container)``,
  JoinLinkFormStyle: css`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 30px 20px 40px;
  `,
  InputWrap: styled(Container)<{ $disabled?: boolean; $isActive?: boolean }>`
    justify-content: center;
    align-items: center;
    padding: 12px 16px;
    height: 50px;
    border: 1px solid ${({ $isActive }) => ($isActive ? 'var(--brand-50)' : 'var(--black-20)')};
    border-radius: 12px;
    background-color: ${({ $disabled }) => ($disabled ? 'var(--black-2)' : 'var(--white)')};
    white-space: nowrap;
    flex: 1;

    & > * {
      font-family: Pretendard, sans-serif;
    }
  `,

  TelecomPrefix: styled(Typography)`
    width: fit-content;
    font-size: 18px;
    font-weight: 400;
    line-height: 25px;
    color: var(--black-40);
  `,

  Divider: styled.div`
    width: 1px;
    height: 18px;
    border: 1px solid var(--black-40);
  `,

  InputStyle: css`
    flex: 1;
    color: var(--black-90);
    font-size: 18px;
    font-weight: 700;
    line-height: 25px;

    &::placeholder {
      color: var(--black-40);
      font-weight: 400;
    }

    &:disabled {
      text-align: center;
      pointer-events: none;
      pointer-cursor: not-allowed;
    }
  `,

  CountDown: styled(Container)`
    width: fit-content;
    font-size: 16px;
    font-weight: 700;
    line-height: 25px;
    color: var(--black-70);
  `,

  ResendButton: styled.button`
    ${ButtonOption3('default')};
    width: 77px;
  `,

  SubmitButton: styled.button`
    ${Noto_heading3_b20};
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 12px 16px;
    width: 100%;
    height: 60px;
    border-radius: 14px;
    background-color: var(--brand-50);
    color: var(--white);
    text-align: center;

    &:not(:disabled) {
      box-shadow: 0 6px 14px 0 rgba(0, 131, 252, 0.3);
    }

    &:disabled {
      background-color: var(--black-30);
    }

    & > span {
      flex: 1;
    }
    & > img {
      width: 24px;
      height: 24px;
    }
  `,
}
